/* eslint-disable style/no-tabs */
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { MdWarningAmber } from "react-icons/md";

import { useStartKyc } from "@/api/mutations";
import FormError from "@/components/FormError";
import H2 from "@/components/H2";
import H3 from "@/components/H3";
import P from "@/components/P";
import RebrandPrimaryButton from "@/components/RebrandPrimaryButton";
import Spacer from "@/components/Spacer";
// import AppState from "@/store/appstate";
import useAppStore from "@/store/appStateZustand";
// import { apiCall } from '@/api/shared';

function Security() {
  const navigate = useNavigate();
  const [agree, setAgree] = useState(false);
  const [gettingKyc, setGettingKyc] = useState(false);
  const [mutationErrorMessage, setMutationErrorMessage] = useState("");
  const [validationErrorMessage, setValidationErrorMessage] = useState("");
  const startKycStatusMutation = useStartKyc();
  const termsCheckboxHandler = () => {
    setAgree(!agree);
  };

  // const kyc = AppState.useState(s => s?.kyc);
  const kyc = useAppStore(s => s.kyc);
  if (kyc?.messages && kyc.messages.length > 0 && kyc.messages[0] === "KYC still pending") {
    navigate({ to: "/security/begin-security-check" });
  }

  if (kyc.kycpassed) {
    navigate({ to: "/" });
  }
  const beginKyc = () => {
    setGettingKyc(true);
    startKycStatusMutation.reset();

    setMutationErrorMessage("");
    setValidationErrorMessage("");
    startKycStatusMutation.mutate(null, {
      onSuccess: (res) => {
        if (res?.kycpassed === true) {
          setValidationErrorMessage("Security Check Already Completed");
        }
        else {
          navigate({ to: "/security/begin-security-check" });
        }
        setGettingKyc(false);
      },
      onError: (error) => {
        setMutationErrorMessage(error.message);
        setGettingKyc(false);
        // AppState.update((s) => {
        //   s.navItemsEnabled = true;
        // });
        useAppStore.setState({ navItemsEnabled: true });
        console.log("start kyc error", error);
      },
    });
  };

  const { t } = useTranslation("security");
  return (
    <div className="flex flex-wrap">
      <div className="w-full bg-sentric-menu-gray lg:min-h-screen-minus-header sentric-shadow-left">
        <div className="mx-4 my-6 lg:mx-12 xl:mx-20 2xl:mx-40">
          <H2>{t("security:security-check-header")}</H2>
          <Spacer />
          <div className="font-sentric-alt bg-white p-8 rounded-lg whitespace-pre-wrap security-text-container grid grid-flow-col grid-auto-columns: minmax(0, 2fr);">
            <div className="hidden py-8 pr-8 security-icon sm:block">
              <MdWarningAmber />
            </div>
            <div>{t("security:security-existing-user-text")}</div>
          </div>
          <Spacer />
          <div className="p-8 bg-white rounded-lg">
            <div>
              <H3>{t("security:security-main-page-header")}</H3>
              <div className="font-sentric-alt">
                <small>
                  {t("security:security-main-page-small-text")}
                  {/* (definitions below which are capitalised are given in The
									General Data Protection Regulation (2016/679, "GDPR")) */}
                </small>
                <Spacer />
                <p>
                  {t("security:security-main-page-text")}
                  {/* We at Sentric Music care about your privacy, therefore, before
									moving forward with this 'Know Your Clients' procedure,
									there's a few things we wanted you to know. */}
                </p>
                <Spacer thin />
                <p>
                  {t("security:security-main-page-text-2")}
                  {/* Sentric are committed to our clients and are constantly
									striving to deliver efficient, tech-based solutions to music
									publishing administration. Whilst efficiency and excellent
									service are important to us, so is integrity and ensuring
									everything we do is done ethically and in the best interest
									not only of our own clients but all creators and musicians
									worldwide. */}
                </p>
                <Spacer thin />
                <p>
                  <strong>
                    {t("security:security-main-page-text-3-header")}
                    {/* WHY ARE WE DOING THIS: */}
                    {" "}
                  </strong>
                  {t("security:security-main-page-text-3")}
                  {/* The purpose of the 'Know Your Clients' process is fraud
									prevention. Sentric wants to ensure we're collecting on behalf
									of and also paying the correct rights holders. Sentric values
									the contribution creators make to this wonderful industry and
									wants to make every effort to ensure that we are always
									working towards making the music industry a fairer place. We
									will use the data to ensure the people who are claiming
									royalties are who they say they are. */}
                </p>
                <Spacer thin />
                <p>
                  {t("security:security-main-page-text-4-header")}
                  {/* By continuing the 'Know Your Clients' process below, you will
									be: */}
                  {" "}
                  <strong>
                    {t("security:security-main-page-text-4")}
                    {/* (i) explicitly consenting to Sentric processing and
										temporarily storing your data as further details in
										Sentric's Privacy Policy; and (ii) explicitly consenting to
										Sentric's partner in this process, IDenfy, also processing
										and storing your data on behalf of Sentric. */}
                  </strong>
                </p>
                <Spacer />
                <p>
                  {t("security:security-main-page-text-5")}
                  {/* Below are the TLDR highlights, but you can find more information in our Privacy Policy */}
                  {" "}
                  <a className="font-semibold underline" href="/privacy-policy">
                    {t("security:security-main-page-here-link")}
                    {/* here */}
                  </a>
                </p>
                <Spacer thin />
                <ul className="pl-8 list-disc">
                  <li>
                    {t("security:security-main-page-list-item-1")}
                    {/* Sentric will be the Data Controller of the data provided
										throughout this 'Know Your Clients' process and will store
										the information for no longer than 6 months. */}
                  </li>
                  <li>
                    {t("security:security-main-page-list-item-2-pt-1")}
                    {/* IDenfy is Sentric's trusted partner for this process and
										will be the Data Processor. No other third parties will hold
										or process the data provided to us during the 'Know Your
										Clients' process. You can find out more about IDenfy's
										security and compliance information */}
                    {" "}
                    <a
                      className="font-semibold underline"
                      href="https://idenfy.com/security"
                    >
                      {t("security:security-main-page-here-link")}
                      {/* here */}
                    </a>
                    {" "}
                    {t("security:security-main-page-list-item-2-pt-2")}
                    {/* and its Privacy Policy */}
                    {" "}
                    <a href="https://idenfy.com/privacy-policy">
                      {t("security:security-main-page-here-link")}
                      {/* here */}
                    </a>

                  </li>
                  <li>
                    {t("security:security-main-page-list-item-3")}
                    {/* The purpose for the processing and storing of your data
										throughout the 'Know Your Clients' process is fraud
										prevention, and your data will not be used for any other
										purpose. */}
                  </li>
                  <li>
                    {t("security:security-main-page-list-item-4")}
                    {/* All of your Individual Rights (i.e. Right to be informed,
										Right to access, Right to rectification, Right to erasure,
										Right to restrict processing, Right to restrict portability,
										Right to object, Rights related to automated decision making
										including profiling) afforded by the GDPR are protected, and
										Sentric's full information is contained in our Privacy
										Policy, should you want to exercise any of these rights. */}
                  </li>
                  <li>
                    {t("security:security-main-page-list-item-5")}
                    {/* You can withdraw your consent at ANY time, and we will
										delete your information immediately. If you wish to withdraw
										your consent, email us at: */}
                    {" "}
                    <a
                      className="font-semibold underline"
                      href="mailto:privacy@sentricmusic.com"
                    >
                      privacy@sentricmusic.com
                    </a>
                  </li>
                </ul>
                <Spacer thin />
                <p>

                  {t("security:security-main-page-ending-1")}
                  {/* Don't worry if you're uncomfortable with the above, please
									contact: */}
                  {" "}
                  <a
                    className="font-semibold underline"
                    href="mailto:privacy@sentricmusic.com"
                  >
                    privacy@sentricmusic.com
                  </a>
                  {" "}
                  {t("security:security-main-page-ending-2")}
                  {/* and we will make every effort to try to ascertain your
									natural person status another way! */}
                </p>

                <p>
                  {t("security:security-main-page-ending-3")}
                  {/* For any enquiries or issues, please contact: */}
                  {" "}
                  <a
                    className="font-semibold underline"
                    href="mailto:privacy@sentricmusic.com"
                  >
                    privacy@sentricmusic.com
                  </a>
                </p>
                <Spacer thin />
                <P>
                  <a
                    href="/privacy-policy"
                    target="_blank"
                    className="text-base font-semibold underline"
                  >
                    {t("security:security-full-terms")}
                  </a>
                </P>
                <Spacer thin />
                <label
                  className="flex items-center cursor-pointer"
                  htmlFor="terms"
                >
                  <input
                    onChange={termsCheckboxHandler}
                    type="checkbox"
                    id="terms"
                    name="terms"
                    className="mr-4 cursor-pointer"
                  />
                  {t("security:security-checkbox-label")}
                </label>
              </div>
            </div>
          </div>
          <Spacer />
          <RebrandPrimaryButton
            disabled={!agree && !gettingKyc}
            onClick={() => beginKyc()}
            arrow
          >
            {t("security:security-check-button")}
          </RebrandPrimaryButton>
          <Spacer />
          <FormError
            mutationError={mutationErrorMessage}
            validationError={validationErrorMessage}
          />
        </div>
      </div>
    </div>
  );
}
export default Security;
